import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  List,
  message,
  Popconfirm,
  Spin,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FunctionOutlined,
} from '@ant-design/icons';
import { modulesAPI } from '../services/api';
import { Menu, Dropdown } from 'antd';

const { TextArea } = Input;
const { Option } = Select;

// Template Variable Button Component
const TemplateVariableButton = ({ inputRef, onInsert, disabled, loading }) => {
  const getTemplateVariables = () => {
    const variables = [];
    
    // System variables header
    variables.push({
      key: 'system-header',
      label: 'System Variables',
      type: 'header'
    });
    
    variables.push(
      { key: '{datetime}', label: 'Current Date/Time', type: 'system' },
      { key: '{date}', label: 'Current Date', type: 'system' },
      { key: '{time}', label: 'Current Time', type: 'system' },
      { key: '{timestamp}', label: 'Unix Timestamp', type: 'system' },
      { key: '{uuid}', label: 'Unique ID', type: 'system' }
    );
    
    // Workflow variables header
    variables.push({
      key: 'workflow-header',
      label: 'Workflow Variables',
      type: 'header'
    });
    
    variables.push(
      { key: '{workflow.id}', label: 'Workflow ID', type: 'workflow' },
      { key: '{workflow.name}', label: 'Workflow Name', type: 'workflow' },
      { key: '{job.id}', label: 'Job ID', type: 'workflow' },
      { key: '{session.id}', label: 'Session ID', type: 'workflow' }
    );
    
    // Context variables header
    variables.push({
      key: 'context-header',
      label: 'Context Variables',
      type: 'header'
    });
    
    variables.push(
      { key: '{context.key}', label: 'Context Value (replace key)', type: 'context' }
    );
    
    // Client variables header
    variables.push({
      key: 'client-header',
      label: 'Client Variables',
      type: 'header'
    });
    
    variables.push(
      { key: '{client.jobprefix}', label: 'Client Job Prefix', type: 'client' },
      { key: '{client.storage_path}', label: 'Client Storage Path', type: 'client' },
      { key: '{client.notification_email}', label: 'Client Notification Email', type: 'client' },
      { key: '{client.name}', label: 'Client Name', type: 'client' }
    );
    
    return variables;
  };

  const handleVariableSelect = (variable) => {
    if (variable.type === 'header') return;
    
    if (inputRef && inputRef.current) {
      const input = inputRef.current.input || inputRef.current;
      
      const cursorStart = input.selectionStart || 0;
      const cursorEnd = input.selectionEnd || cursorStart;
      const currentValue = input.value || '';
      
      const newValue = 
        currentValue.slice(0, cursorStart) + 
        variable.key + 
        currentValue.slice(cursorEnd);
      
      input.value = newValue;
      
      const inputEvent = new Event('input', { bubbles: true });
      const changeEvent = new Event('change', { bubbles: true });
      input.dispatchEvent(inputEvent);
      input.dispatchEvent(changeEvent);
      
      if (onInsert) {
        onInsert(newValue);
      }
      
      const newPosition = cursorStart + variable.key.length;
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(newPosition, newPosition);
      }, 10);
    } else if (onInsert) {
      onInsert(variable.key);
    }
  };

  const menu = (
    <Menu
      style={{ maxHeight: '300px', overflowY: 'auto', minWidth: '250px' }}
      onClick={({ key }) => {
        const variable = getTemplateVariables().find(v => v.key === key);
        if (variable) {
          handleVariableSelect(variable);
        }
      }}
    >
      {getTemplateVariables().map(variable => 
        variable.type === 'header' ? (
          <Menu.ItemGroup key={variable.key} title={variable.label} />
        ) : (
          <Menu.Item key={variable.key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{variable.key}</span>
              <span style={{ fontSize: '11px', color: '#666', marginLeft: '8px' }}>
                {variable.label}
              </span>
            </div>
          </Menu.Item>
        )
      )}
    </Menu>
  );

  return (
    <Dropdown overlay={menu} trigger={['click']} disabled={disabled || loading}>
      <Button
        icon={<FunctionOutlined />}
        size="small"
        style={{ border: 'none', background: 'transparent' }}
        title="Insert template variable"
        disabled={disabled || loading}
      />
    </Dropdown>
  );
};

// Highlighted Input component for context keys
const HighlightedInput = React.forwardRef(({ value, onChange, ...props }, ref) => {
  const inputRef = useRef(null);
  const highlightRef = useRef(null);
  const [inputValue, setInputValue] = useState(value || '');
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onChange) {
      onChange(e);
    }
  };

  const handleScroll = (e) => {
    setScrollLeft(e.target.scrollLeft);
  };

  const handleTemplateInsert = (newValue) => {
    if (onChange) {
      onChange({ target: { value: newValue } });
    }
  };

  const getHighlightedText = (text) => {
    if (!text) return '';
    
    // Match context keys like {context.key} or {variable}
    const parts = text.split(/({[^}]+})/);
    
    return parts.map((part, index) => {
      if (part.match(/^{[^}]+}$/)) {
        return `<mark style="background-color: #e6f7ff; color: #1890ff; padding: 2px 4px; border-radius: 3px; font-weight: 500;">${part}</mark>`;
      }
      return part;
    }).join('');
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div
        ref={highlightRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          padding: '4px 11px',
          border: '1px solid transparent',
          fontSize: '14px',
          lineHeight: '1.5715',
          color: 'transparent',
          whiteSpace: 'pre',
          overflow: 'hidden',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
          transform: `translateX(-${scrollLeft}px)`
        }}
        dangerouslySetInnerHTML={{ __html: getHighlightedText(inputValue) }}
      />
      <Input
        {...props}
        ref={inputRef}
        value={inputValue}
        onChange={handleChange}
        onScroll={handleScroll}
        suffix={
          <TemplateVariableButton 
            inputRef={inputRef}
            onInsert={handleTemplateInsert}
          />
        }
        style={{
          ...props.style,
          position: 'relative',
          background: 'transparent',
          color: inputValue && inputValue.includes('{') ? 'transparent' : 'inherit',
          caretColor: 'black'
        }}
      />
    </div>
  );
});

// Highlighted TextArea component for context keys
const HighlightedTextArea = React.forwardRef(({ value, onChange, ...props }, ref) => {
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);
  const [textValue, setTextValue] = useState(value || '');
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    setTextValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setTextValue(newValue);
    if (onChange) {
      onChange(e);
    }
  };

  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop);
    setScrollLeft(e.target.scrollLeft);
  };

  const handleTemplateInsert = (newValue) => {
    if (onChange) {
      onChange({ target: { value: newValue } });
    }
  };

  const getHighlightedText = (text) => {
    if (!text) return '';
    
    // Match context keys like {context.key} or {variable}
    const parts = text.split(/({[^}]+})/);
    
    return parts.map((part, index) => {
      if (part.match(/^{[^}]+}$/)) {
        return `<mark style="background-color: #e6f7ff; color: #1890ff; padding: 2px 4px; border-radius: 3px; font-weight: 500;">${part}</mark>`;
      }
      return part;
    }).join('');
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div
        ref={highlightRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          padding: '4px 11px',
          border: '1px solid transparent',
          fontSize: '14px',
          lineHeight: '1.5715',
          color: 'transparent',
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
          transform: `translate(-${scrollLeft}px, -${scrollTop}px)`,
          wordBreak: 'break-word'
        }}
        dangerouslySetInnerHTML={{ __html: getHighlightedText(textValue) }}
      />
      <TextArea
        {...props}
        ref={textareaRef}
        value={textValue}
        onChange={handleChange}
        onScroll={handleScroll}
        style={{
          ...props.style,
          position: 'relative',
          background: 'transparent',
          color: textValue && textValue.includes('{') ? 'transparent' : 'inherit',
          caretColor: 'black'
        }}
      />
      <div style={{ 
        position: 'absolute', 
        top: '8px', 
        right: '8px', 
        zIndex: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '4px',
        padding: '2px'
      }}>
        <TemplateVariableButton 
          inputRef={textareaRef}
          onInsert={handleTemplateInsert}
        />
      </div>
    </div>
  );
});

const NodeModuleEditor = () => {
  const [modules, setModules] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fieldTypes = [
    'string',
    'number',
    'integer',
    'boolean',
    'select',
    'multiselect',
    'textarea',
    'key_value'
  ];

  // Helper function to parse options string "key1:label1, key2:label2" to object
  const parseOptionsString = (optionsStr) => {
    if (!optionsStr) return null;
    
    try {
      const options = {};
      const pairs = optionsStr.split(',').map(pair => pair.trim());
      
      for (const pair of pairs) {
        const [key, label] = pair.split(':').map(part => part.trim());
        if (key && label) {
          options[key] = label;
        } else if (key) {
          // If no label provided, use key as label
          options[key] = key;
        }
      }
      
      return Object.keys(options).length > 0 ? options : null;
    } catch (error) {
      console.error('Error parsing options string:', error);
      return null;
    }
  };

  // Helper function to convert options object back to string for editing
  const formatOptionsForEdit = (options) => {
    if (!options || typeof options !== 'object') return '';
    
    return Object.entries(options)
      .map(([key, label]) => `${key}:${label}`)
      .join(', ');
  };

  // Load modules from database
  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    setLoading(true);
    try {
      const response = await modulesAPI.getModules();
      setModules(response.data);
    } catch (error) {
      message.error('Failed to load modules');
      console.error('Error loading modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (module = null) => {
    setEditingModule(module);
    if (module) {
      // Convert options object back to string format for editing
      const fieldsForEdit = (module.fields || []).map(field => ({
        ...field,
        options: field.field_type === 'select' && field.options 
          ? formatOptionsForEdit(field.options)
          : field.options
      }));
      
      form.setFieldsValue({
        name: module.name,
        description: module.description,
        category: module.category,
        fields: fieldsForEdit,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ fields: [] });
    }
    setIsModalOpen(true);
  };

  const handleSave = async (values) => {
    setSubmitting(true);
    try {
      const moduleData = {
        name: values.name,
        description: values.description,
        category: values.category || 'general',
        fields: (values.fields || []).map(field => ({
          ...field,
          // Parse options from key:label format to object
          options: field.field_type === 'select' && field.options 
            ? parseOptionsString(field.options)
            : field.options
        }))
      };

      if (editingModule) {
        // Update existing module
        const response = await modulesAPI.updateModule(editingModule.id, moduleData);
        setModules(modules.map(m => m.id === editingModule.id ? response.data : m));
        message.success('Module updated successfully');
      } else {
        // Create new module
        const response = await modulesAPI.createModule(moduleData);
        setModules([...modules, response.data]);
        message.success('Module created successfully');
      }

      setIsModalOpen(false);
      form.resetFields();
      setEditingModule(null);
    } catch (error) {
      message.error('Failed to save module');
      console.error('Error saving module:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteModule = async (moduleId) => {
    try {
      await modulesAPI.deleteModule(moduleId);
      setModules(modules.filter(m => m.id !== moduleId));
      message.success('Module deleted successfully');
    } catch (error) {
      message.error('Failed to delete module');
      console.error('Error deleting module:', error);
    }
  };

  const renderFieldType = (type) => {
    const typeColors = {
      string: 'blue',
      number: 'green',
      integer: 'cyan',
      boolean: 'orange',
      select: 'purple',
      regex_transform_list: 'magenta',
      key_value: 'geekblue'
    };
    return <Tag color={typeColors[type] || 'default'}>{type}</Tag>;
  };

  return (
    <div>
      <Card
        title="Node Module Editor"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            Create New Module
          </Button>
        }
      >
        <Spin spinning={loading}>
          <List
            grid={{ gutter: 16, column: 2 }}
            dataSource={modules}
            renderItem={(module) => (
              <List.Item>
                <Card
                  size="small"
                  title={module.name}
                  extra={<Tag color="blue">{module.category}</Tag>}
                  actions={[
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => openModal(module)}
                    >
                      Edit
                    </Button>,
                    <Popconfirm
                      title="Delete this module?"
                      onConfirm={() => deleteModule(module.id)}
                    >
                      <Button size="small" icon={<DeleteOutlined />} danger>
                        Delete
                      </Button>
                    </Popconfirm>,
                  ]}
                >
                  <p>{module.description}</p>
                  <div>
                    <strong>Fields ({module.fields?.length || 0}):</strong>
                    <div style={{ marginTop: 8 }}>
                      {(module.fields || []).map((field, index) => (
                        <div key={index} style={{ marginBottom: 4 }}>
                          <strong>{field.name}</strong> ({field.label || field.name}) - {renderFieldType(field.field_type)}
                          {field.required && <span style={{ color: 'red' }}> *</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
            locale={{ emptyText: 'No modules created yet' }}
          />
        </Spin>
      </Card>

      <Modal
        title={editingModule ? 'Edit Module' : 'Create New Module'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
        centered
        style={{ top: 20 }}
        
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{ fields: [] }}
        >
          <Form.Item
            name="name"
            label="Module Name"
            rules={[{ required: true, message: 'Please enter module name' }]}
          >
            <HighlightedInput placeholder="Enter module name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <HighlightedTextArea rows={3} placeholder="Enter module description" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
          >
            <Select placeholder="Select category">
              <Option value="general">General</Option>
              <Option value="data">Data Processing</Option>
              <Option value="communication">Communication</Option>
              <Option value="file">File Operations</Option>
              <Option value="utility">Utility</Option>
            </Select>
          </Form.Item>

          <Form.List name="fields">
            {(fields, { add, remove }) => (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h4>Custom Fields</h4>
                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                    Add Field
                  </Button>
                </div>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto auto', gap: 8, alignItems: 'end' }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        label="Field Key"
                        rules={[
                          { required: true, message: 'Missing field key' },
                          { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: 'Field key must be valid identifier' }
                        ]}
                      >
                        <HighlightedInput placeholder="field_key" />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'label']}
                        label="Field Label"
                        rules={[{ required: true, message: 'Missing field label' }]}
                      >
                        <HighlightedInput placeholder="Field Label" />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'field_type']}
                        label="Type"
                        rules={[{ required: true, message: 'Missing field type' }]}
                      >
                        <Select placeholder="Select type">
                          {fieldTypes.map(type => (
                            <Option key={type} value={type}>{type}</Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'required']}
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                      >
                        <div>
                          <label>
                            <input type="checkbox" /> Required
                          </label>
                        </div>
                      </Form.Item>

                      <Button
                        type="text"
                        danger
                        onClick={() => remove(name)}
                        icon={<DeleteOutlined />}
                      />
                    </div>

                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) => {
                        const prevType = prevValues.fields?.[name]?.field_type;
                        const currentType = currentValues.fields?.[name]?.field_type;
                        return prevType !== currentType;
                      }}
                    >
                      {({ getFieldValue }) => {
                        const fieldType = getFieldValue(['fields', name, 'field_type']);
                        return fieldType === 'select' ? (
                          <Form.Item
                            {...restField}
                            name={[name, 'options']}
                            label="Options (key:label format, comma-separated)"
                            rules={[{ required: true, message: 'Please provide options for select field' }]}
                          >
                            <HighlightedInput placeholder="key1:Label 1, key2:Label 2, key3:Label 3" />
                          </Form.Item>
                        ) : null;
                      }}
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, 'default_value']}
                      label="Default Value (optional)"
                    >
                      <HighlightedInput placeholder="Default value" />
                    </Form.Item>
                  </Card>
                ))}
              </div>
            )}
          </Form.List>

          <div style={{ display: 'flex', justifyContent: 'end', gap: 8, marginTop: 24 }}>
            <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingModule ? 'Update' : 'Create'} Module
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default NodeModuleEditor;
